import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBriefcase, faUsers, faHardHat, faIdCard, faClock, faBook, faClipboardList, faMoneyCheckAlt, 
  faMoneyBillWave, faHandsHelping, faImage, faEye, faStar, faQrcode, faUser, faLockOpen,
  faSadCry, faWallet, faSearch, faClockRotateLeft, faClipboardQuestion, faCertificate, faToolbox, 
  faQuestionCircle, faBrain, faShare, faSave, faPrint
} from '@fortawesome/free-solid-svg-icons';

function BlueHandsConsultants() {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [category, setCategory] = useState("Labour");
  const [searchCity, setSearchCity] = useState("Mumbai");
  const [customCity, setCustomCity] = useState("");
  const [jobsData, setJobsData] = useState([]);
  const [isInstant, setIsInstant] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [applyRemarks, setApplyRemarks] = useState("");
  const [searchClicked, setSearchClicked] = useState(false);
  const [shortlistedRemarks, setShortlistedRemarks] = useState("");
  const [shortlisted, setShortlisted] = useState(false);
  const [applied, setApplied] = useState(false);

  const fetchJobs = useCallback(() => {
    const config = {
      url: "/bluejobs/jobs",
      method: "post",
      headers: { Authorization: authState.token },
      data: {
        userId: userId, 
        searchKeyword: searchKeyword, 
        category: category, 
        searchCity: searchCity, 
        customCity: customCity,
        isInstant: isInstant
      },
    };

    fetchData(config, { showSuccessToast: false })
      .then((data) => {
        if (data && data.jobsData) {
          setJobsData(data.jobsData);
        } else {
          console.error("Data not found!!");
          setJobsData([]); 
        }
      })
      .catch((error) => {
        console.error("Error fetching Job data:", error);
        setJobsData([]); 
      });
  }, [
    authState.token,
    userId,
    searchCity,
    customCity,
    isInstant,
    searchKeyword,
    category,
    fetchData
  ]);

  const handleView = (jobId) => {
    setSelectedJobId(prevId => (prevId === jobId ? null : jobId));
  };

  const handleJobShare = (jobId) => {
    setSelectedJobId(jobId);
  };

  const handleJobSave = (jobId) => {
    setSelectedJobId(jobId);
  };

  const saveShortlist = useCallback(
    (jobid, jobcode, jobtitle, employername, postedby, posteddate) => {
      const config = {
        url: "/bluejobsshortlisted/shortlist",
        method: "post",
        headers: { Authorization: authState.token },
        data: {
          userId: userId,
          userCode: userCode,
          jobCode: jobcode,
          jobId: jobid,
          jobTitle: jobtitle,
          jobPostedBy: postedby,
          postedDate: posteddate,
          employerName: employername,
          shortlistedDate: new Date(),
          shortlistedRemarks, 
          isActive: true,
        },
      };
  
      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          setShortlisted(true); 
        })
        .catch((error) => {
          setShortlisted(false);
          console.error("Error applying this job:", error);
        });
    },
    [authState.token, userId, userCode, fetchData, shortlistedRemarks]
  );

  const saveApply = useCallback(
    (jobid, jobcode, jobtitle, employername, postedby, posteddate) => {
      const config = {
        url: "/bluejobsapply/apply",
        method: "post",
        headers: { Authorization: authState.token },
        data: {
          userId,
          userCode,
          jobCode: jobcode,
          jobTitle: jobtitle,
          jobId: jobid,
          jobPostedBy: postedby,
          postedDate: posteddate,
          employerName: employername,
          applyDate: new Date(),
          applyRemarks, 
          isWithdraw: false,
          reviewerId: null,
          reviewerCode: '',
          reviewDate: null,
          reviewRemarks: '',
          reviewStatus: '',
          isHide: false,
        },
      };
  
      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          setApplied(true); 
        })
        .catch((error) => {
          console.error("Error applying this job:", error);
        });
    },
    [authState.token, userId, userCode, fetchData, applyRemarks]
  );

  const formatDateTime = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const formatDate = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const JobList = ({ jobs, category, isInstant, searchKeyword, searchCity, customCity }) => (
    <div className="flex flex-col items-center justify-center w-full">
      <p className="text-yellow-200 text-lg mb-4">
        {jobs.length} {category} {isInstant ? "Instant" : "Regular"} Jobs Found for {searchKeyword || 'any keywords'} in {searchCity} {customCity || ''}
      </p>
      {jobs.map((job, index) => (
        <JobCard
          key={job._id}
          job={job}
          index={index}
          selectedJobId={selectedJobId}
          onView={handleView}
          onShare={handleJobShare}
          onSave={handleJobSave}
          saveShortlist={saveShortlist}
          saveApply={saveApply}
          shortlisted={shortlisted}
          applied={applied}
        />
      ))}
    </div>
  );

  const JobCard = ({ job, index, selectedJobId, onView, onShare, onSave, saveShortlist, saveApply, shortlisted, applied }) => (
    <div className="bg-gray-800 my-2 p-3 text-gray-200 rounded-lg shadow-lg text-sm w-full max-w-md mx-auto">
      <div className="flex flex-row items-center justify-between">
        <span className="text-base font-medium">
          {index + 1}. {job.jobTitle} ({formatDateTime(job.datePosted)})
        </span>
        <button
          onClick={() => onView(job._id)}
          className="text-blue-400 hover:text-blue-300 w-8 ml-4 p-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="View details"
        >
          <FontAwesomeIcon icon={faEye} size="sm" />
        </button>
      </div>
      {selectedJobId === job._id && (
        <JobDetails 
          job={job} 
          onShare={onShare} 
          onSave={onSave} 
          saveShortlist={saveShortlist}
          saveApply={saveApply}
          shortlisted={shortlisted}
          applied={applied}
        />
      )}
    </div>
  );

  const JobDetails = ({ job, onShare, onSave, saveShortlist, saveApply, shortlisted, applied }) => (
    <div className="bg-gray-700 text-gray-100 px-4 py-4 mt-2 rounded-lg">
      <h2 className="text-yellow-200 text-lg font-bold mb-2">Job Details of {job.jobTitle}</h2>
      <div className="space-y-1 text-sm mb-4">
        <p><strong>Job Code:</strong> {job.jobCode}</p>
        <p><strong>Location:</strong> {job.jobLocation}</p>
        <p><strong>Category:</strong> {job.jobCategory}</p>
        <p><strong>Description:</strong> {job.jobDescription}</p>
        <p><strong>Vacancies:</strong> {job.vacancies}</p>
        <p><strong>Salary Details:</strong> {job.salaryDetails}</p>
        <p><strong>Remarks:</strong> {job.splRemarks}</p>
        <p><strong>Employer:</strong> {job.employerName}</p>
        <p>{job.employerDetails}</p>
        <p>{job.employerAddress}</p>
        <p><strong>Department:</strong> {job.department}</p>
        <p><strong>Application Period:</strong> {formatDate(job.applyFromDate)} to {formatDate(job.applyToDate)}</p>
        <p><strong>Date Posted:</strong> {formatDate(job.datePosted)}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <button
          className="bg-purple-700 text-white px-4 py-2 rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
          onClick={() => saveShortlist(job._id, job.jobCode, job.jobTitle, job.employerName, job.createdBy, job.datePosted)}
          disabled={shortlisted}
        >
          Shortlist
        </button>
        <button
          className="bg-blue-700 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
          onClick={() => saveApply(job._id, job.jobCode, job.jobTitle, job.employerName, job.createdBy, job.datePosted)}
          disabled={applied}
        >
          Apply
        </button>
      </div>
      {shortlisted && (
        <div className="mb-2 text-green-400 text-sm">
          You Have Shortlisted The Job!
        </div>
      )}
      {applied && (
        <div className="mb-2 text-green-400 text-sm">
          You Have Successfully Applied For The Job!
        </div>
      )}
      <div className="flex justify-center gap-4 pt-2 border-t border-gray-600">
        <button
          onClick={() => onShare(job._id)}
          className="text-green-400 hover:text-green-300 p-1 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
          title="Share"
        >
          <FontAwesomeIcon icon={faShare} size="sm" />
        </button>
        <button
          onClick={() => onSave(job._id)}
          className="text-blue-400 hover:text-blue-300 p-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="Shortlist"
        >
          <FontAwesomeIcon icon={faSave} size="sm" />
        </button>
        <button
          onClick={() => saveApply(job._id, job.jobCode, job.jobTitle, job.employerName, job.createdBy, job.datePosted)}
          className="text-orange-400 hover:text-orange-300 p-1 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
          title="Apply"
          disabled={applied}
        >
          <FontAwesomeIcon icon={faPrint} size="sm" />
        </button>
      </div>
    </div>
  );

  const NoJobsMessage = ({ isInstant, category, searchKeyword, searchCity, customCity }) => (
    <div className="text-center py-8">
      <p className="text-yellow-300 text-lg">
        No {isInstant ? "Instant" : "Regular"} {category} Jobs Found for {searchKeyword || 'any keywords'} in {searchCity} {customCity || ''}
      </p>
    </div>
  );

  const Tooltip = ({ text, children, position = "top" }) => (
    <div className="relative group">
      {children}
      <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap {position === 'top' ? '-top-8 left-1/2 transform -translate-x-1/2' : 'bottom-0 left-1/2 transform -translate-x-1/2'}">
        {text}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-gray-100">
      <div className="py-4 px-2 sm:px-4">
        <h2 className="text-2xl font-bold text-center mb-6 text-white">Global Workforce Jobboard</h2>
        <div className="max-w-md mx-auto bg-gray-900 rounded-lg p-4 sm:p-6 border-2 border-blue-800 shadow-xl">
          <div className="flex items-center mb-4">
            <FontAwesomeIcon icon={faSearch} className="text-blue-400 text-lg mr-2 flex-shrink-0" />
            <input
              type="text"
              placeholder="Enter Keywords eg Electrician, Locality Name etc"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="flex-grow h-10 bg-gray-800 text-white border border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-3 mb-4">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full h-10 bg-gray-800 text-white border border-gray-600 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a Category</option>
              <optgroup label="Industrial & Manufacturing">
                <option value="Factory Worker">Factory Worker</option>
                <option value="Machine Operator">Machine Operator</option>
                <option value="Welder">Welder</option>
              </optgroup>
              <optgroup label="Construction & Infrastructure">
                <option value="Mason">Mason</option>
                <option value="Carpenter">Carpenter</option>
                <option value="Electrician">Electrician</option>
                <option value="Plumber">Plumber</option>
              </optgroup>
              <optgroup label="Agriculture & Farming">
                <option value="Farm Labourer">Farm Labourer</option>
                <option value="Harvesting Worker">Harvesting Worker</option>
                <option value="Irrigation Technician">Irrigation Technician</option>
              </optgroup>
              <optgroup label="Transportation & Logistics">
                <option value="Truck Driver">Truck Driver</option>
                <option value="Warehouse Worker">Warehouse Worker</option>
              </optgroup>
              <optgroup label="Mining & Extraction">
                <option value="Coal Miner">Coal Miner</option>
                <option value="Oil Rig Worker">Oil Rig Worker</option>
              </optgroup>
              <optgroup label="Domestic & Cleaning">
                <option value="Housekeeper">Housekeeper</option>
                <option value="Janitor">Janitor</option>
              </optgroup>
              <optgroup label="Security & Public Safety">
                <option value="Security Guard">Security Guard</option>
                <option value="Firefighter">Firefighter</option>
              </optgroup>
              <optgroup label="Hospitality & Food Service">
                <option value="Kitchen Helper">Kitchen Helper</option>
                <option value="Dishwasher">Dishwasher</option>
              </optgroup>
              <optgroup label="Textile & Garment">
                <option value="Tailor">Tailor</option>
                <option value="Weaver">Weaver</option>
              </optgroup>
              <optgroup label="General & Unskilled Labour">
                <option value="Construction Helper">Construction Helper</option>
                <option value="Daily Wage Labourer">Daily Wage Labourer</option>
              </optgroup>
              <optgroup label="Other Roles">
                <option value="Highly Skilled">Highly Skilled</option>
                <option value="Labour">Labour</option>
                <option value="Contractor">Contractor</option>
                <option value="Consultant">Consultant</option>
                <option value="Company">Company</option>
                <option value="Supervisor">Supervisor</option>
              </optgroup>
            </select>

            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                className="flex-1 h-10 bg-gray-800 text-white border border-gray-600 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="UAE">UAE</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Qatar">Qatar</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Oman">Oman</option>
                <option value="Bahrain">Bahrain</option>
                <option value="India">India</option>
                <option value="">Other Location</option>
              </select>
            </div>

            {searchCity === "" && (
              <input
                type="text"
                placeholder="Enter location"
                value={customCity}
                onChange={(e) => setCustomCity(e.target.value)}
                className="h-10 bg-gray-800 text-white border border-gray-600 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}

            <label className="flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="mr-2 w-4 h-4 accent-blue-600 focus:ring-2 focus:ring-blue-500"
                checked={isInstant} 
                onChange={(e) => setIsInstant(e.target.checked)} 
              />
              <span className="text-sm font-medium text-gray-200">Get Instant Job</span>
            </label>
          </div>

          <button
            onClick={fetchJobs}
            disabled={loading}
            className="w-full bg-blue-800 text-white py-3 rounded-md hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>

          <div className="mt-6">
            {jobsData.length > 0 ? (
              <JobList
                jobs={jobsData}
                category={category}
                isInstant={isInstant}
                searchKeyword={searchKeyword}
                searchCity={searchCity}
                customCity={customCity}
              />
            ) : searchClicked ? (
              <NoJobsMessage
                isInstant={isInstant}
                category={category}
                searchKeyword={searchKeyword}
                searchCity={searchCity}
                customCity={customCity}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlueHandsConsultants;